import { ErrorBoundary } from '@common/components/feedback/ErrorBoundary';
import { isDev } from '@config/env';
import i18n, { displayDateFormat } from '@config/i18n';
import { queryClient } from '@config/queryClient';
import { DatePickerProvider, TableFilterProvider, ToastProvider } from '@ntconsult/aluma-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { enUS, ptBR } from 'date-fns/locale';
import type { ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <TranslatedTableFilters>
            <TranslatedDatePickers>
              <ToastProvider>{children}</ToastProvider>
            </TranslatedDatePickers>
          </TranslatedTableFilters>
        </I18nextProvider>
        {isDev && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

/** Textos dos filtros de coluna da `Table` (Aluma 2.1) no idioma ativo — sem isso ficam fixos em pt-BR. */
function TranslatedTableFilters({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <TableFilterProvider
      messages={{
        trigger: (column, active) =>
          active
            ? t('tableFilters.triggerActive', { column })
            : t('tableFilters.trigger', { column }),
        title: (column) => t('tableFilters.title', { column }),
        apply: t('tableFilters.apply'),
        clear: t('tableFilters.clear'),
        textPlaceholder: t('tableFilters.textPlaceholder'),
        textInputLabel: (column) => t('tableFilters.textInputLabel', { column }),
        optionsSearchPlaceholder: t('tableFilters.optionsSearchPlaceholder'),
        optionsSearchLabel: (column) => t('tableFilters.optionsSearchLabel', { column }),
        optionsEmpty: (query) => t('tableFilters.optionsEmpty', { query }),
        min: t('tableFilters.min'),
        max: t('tableFilters.max'),
      }}
    >
      {children}
    </TableFilterProvider>
  );
}

/** Locale, formato e textos do `DatePicker` (Aluma) no idioma ativo — o default é fixo em pt-BR. */
function TranslatedDatePickers({ children }: { children: ReactNode }) {
  const { t, i18n: instance } = useTranslation('common');
  const isEnglish = instance.language === 'en-US';
  return (
    <DatePickerProvider
      locale={isEnglish ? enUS : ptBR}
      format={displayDateFormat(instance.language)}
      messages={{
        previousMonth: t('datePicker.previousMonth'),
        nextMonth: t('datePicker.nextMonth'),
        previousYear: t('datePicker.previousYear'),
        nextYear: t('datePicker.nextYear'),
        previousDecade: t('datePicker.previousDecade'),
        nextDecade: t('datePicker.nextDecade'),
        today: t('datePicker.today'),
        clear: t('datePicker.clear'),
        openCalendar: t('datePicker.openCalendar'),
        closeCalendar: t('datePicker.closeCalendar'),
        selectDate: t('datePicker.selectDate'),
        selectRange: t('datePicker.selectRange'),
        selectMonth: t('datePicker.selectMonth'),
        selectYear: t('datePicker.selectYear'),
        selectQuarter: t('datePicker.selectQuarter'),
        selectWeek: t('datePicker.selectWeek'),
        selectTime: t('datePicker.selectTime'),
        monthYearDropdown: t('datePicker.monthYearDropdown'),
      }}
    >
      {children}
    </DatePickerProvider>
  );
}
